import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectInitialRegistrationDate } from "@App/state/selectors/orders";
import { setRegistrationDate } from "@App/state/action-creators";

const pad2 = (n: number) => String(n).padStart(2, "0");

/** Accepts "MM-YYYY" or "DD-MM-YYYY". Defaults day=15 for "MM-YYYY". */
const parseInitialDate = (value: string): Date | null => {
  const parts = value.split("-").map(Number);
  if (parts.length === 2) {
    const [month, year] = parts;
    if (!month || !year) return null;
    return new Date(year, month - 1, 15);
  }
  if (parts.length === 3) {
    const [day, month, year] = parts;
    if (!day || !month || !year) return null;
    return new Date(year, month - 1, day);
  }
  return null;
};

const formatInitialDate = (d: Date) =>
  `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}`;

export const useRegistrationDateParameter = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  const initialRegistrationDate = useSelector(selectInitialRegistrationDate);
  const initialRegistrationDateQuery = searchParams.get("initialRegistrationDate");

  useEffect(() => {
    if (!initialRegistrationDate && initialRegistrationDateQuery) {
      const parsed = parseInitialDate(initialRegistrationDateQuery);
      if (parsed) {
        dispatch(setRegistrationDate(parsed));
      }
    }
  }, [dispatch, initialRegistrationDate, initialRegistrationDateQuery]);

  const registrationDateParameter = useMemo(
    () =>
      initialRegistrationDate
        ? { initialRegistrationDate: formatInitialDate(initialRegistrationDate) }
        : {},
    [initialRegistrationDate],
  );

  return { registrationDateParameter };
};
